import React, { useCallback, useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';
import axios from 'axios';
import { Theme } from '@app/theme';
import {
  Document,
  useConfirmUpload,
  useCreateDocumentUploadUrl,
  useDocuments,
} from '@app/api';
import { Dropzone } from './Dropzone';
import { UploadedDocument } from './UploadedDocument';
import { useToast } from '@app/core';
import moment from 'moment';
import { ChildProcess } from 'child_process';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {},
  fileList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase,
    marginTop: theme.marginBase * 2,
  },
}));

interface Props {
  sessionId?: string;
  participationId?: string;
  disabled?: boolean;
}

interface UploadingDocument {
  progress?: number;
  document: Document;
}

export const DocumentsDropzone = ({
  disabled,
  sessionId,
  participationId,
}: Props) => {
  const classes = useStyles();
  const attachment = { sessionId, participationId };
  const toast = useToast();

  const [uploadingDocuments, setUploadingDocuments] = useState<
    Record<string, UploadingDocument>
  >({});

  const { data: documents } = useDocuments(attachment);
  const { mutateAsync: createUploadUrl } =
    useCreateDocumentUploadUrl(attachment);
  const { mutateAsync: confirmUpload } = useConfirmUpload(attachment);

  const onUploadProgress = useCallback(
    (progress: number, document: Document) => {
      setUploadingDocuments((prev) => ({
        ...prev,
        [document.id]: {
          document,
          progress: progress === 100 ? undefined : progress,
        },
      }));
    },
    [setUploadingDocuments],
  );

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (!acceptedFiles.length) {
        return;
      }
      await Promise.all(
        acceptedFiles.map(async (file) => {
          try {
            const extension = file.name.split('.').pop() || 'pdf';
            const { url, document } = await createUploadUrl({
              contentType: file.type,
              extension,
              originalName: file.name,
            });
            setUploadingDocuments((prev) => ({
              ...prev,
              [document.id]: { document, progress: 0 },
            }));

            await axios({
              url: url,
              method: 'PUT',
              data: file,
              headers: { 'Content-Type': file.type },
              maxContentLength: 100 * 1024 * 1024 * 1024,
              timeout: 30 * 60 * 1000, //30mins
              onUploadProgress: (pevt) =>
                onUploadProgress(
                  Math.round((pevt.loaded / (pevt?.total || 100)) * 100),
                  document,
                ),
            });
            await confirmUpload(document.id);
            setTimeout(() => {
              setUploadingDocuments((prev) => {
                const copy = { ...prev };
                delete copy[document.id];
                return copy;
              });
            }, 500);
          } catch (error) {
            toast.exception(error);
          }
        }),
      );
    },
    [createUploadUrl, confirmUpload, toast, onUploadProgress],
  );

  const dedupedDocuments = useMemo(() => {
    const byIds = { ...uploadingDocuments };
    if (documents) {
      for (const doc of documents) {
        byIds[doc.id] = {
          document: doc,
          progress: doc.uploadConfirmed ? undefined : byIds[doc.id]?.progress,
        };
      }
    }

    return Object.values(byIds).sort((a, b) =>
      moment(b.document.createdAt).diff(a.document.createdAt),
    );
  }, [documents, uploadingDocuments]);

  return (
    <div className={classes.container}>
      <Dropzone format="documents" onDrop={onDrop} disabled={disabled} />
      <div className={classes.fileList}>
        {dedupedDocuments.map(({ document, progress }) => (
          <UploadedDocument
            key={document.id}
            document={document}
            progress={progress}
          />
        ))}
      </div>
    </div>
  );
};
